<!--
  Patient Registry — select Patient Flags, get the patients carrying ALL of them.

  HEIGHT: h-full, NOT a 100vh calc. The parent chain is fully height-constrained
  (App.vue h-screen > flex-1 overflow-hidden > h-full scroll container >
  CareManagement_Overview h-full flex flex-col > flex-1 overflow-auto min-h-0),
  so this component inherits a correct box. A viewport calc would double-count
  the header/tab chrome AND ignore the wrapper padding, overflowing the screen.
  min-h-0 lets the inner panels' overflow-y-auto actually scroll inside a flex
  parent rather than expanding it.

  Left column: available flags (click to add) over an Active Flags tray + Query.
  Right: the resulting patient list. The query is EXPLICIT — selecting flags
  stages criteria, clicking Query runs the intersection. Criteria and results
  are therefore never half-in-sync, and a large cohort isn't re-queried on
  every click.

  Data:
    GET  /api/patient-registry/flags/  -> available flags + live ON counts
    POST /api/patient-registry/        -> { flag_ids, page, page_size }
-->
<template>
  <div class="flex h-full min-h-0">

    <!-- LEFT: available flags (top) + active flags (bottom) -->
    <div class="w-72 shrink-0 border-r border-gray-200 flex flex-col min-h-0 bg-white">

      <div class="px-3 py-2 border-b border-gray-200 shrink-0">
        <div class="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">
          Available Flags
        </div>
        <input
          v-model="flagSearch"
          type="text"
          placeholder="Filter flags..."
          class="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      <div class="flex-1 overflow-y-auto min-h-0">
        <div v-if="flagsLoading" class="px-3 py-2 text-xs text-gray-500">Loading flags...</div>
        <div v-else-if="flagsError" class="px-3 py-2 text-xs text-red-600">
          {{ flagsError }}
          <button class="underline ml-1" @click="fetchFlags">Retry</button>
        </div>
        <div v-else-if="!availableFlags.length" class="px-3 py-2 text-xs text-gray-500">
          {{ flags.length ? 'All flags active.' : 'No flags.' }}
        </div>

        <button
          v-for="f in availableFlags"
          :key="f.flag_id"
          type="button"
          class="w-full flex items-center gap-2 px-3 py-1.5 border-b border-gray-100 text-left hover:bg-gray-50"
          @click="addFlag(f.flag_id)"
        >
          <span class="w-2 h-2 rounded-full shrink-0" :class="dotClass(f.color)"></span>
          <span class="flex-1 min-w-0 text-xs text-gray-800 truncate" :title="f.label">
            {{ f.label }}
          </span>
          <span class="text-[11px] tabular-nums text-gray-500 shrink-0">
            {{ formatNumber(f.on_count) }}
          </span>
        </button>
      </div>

      <!-- Active flags tray -->
      <div class="border-t-2 border-gray-300 bg-gray-50 shrink-0 flex flex-col min-h-0" style="max-height: 20rem;">
        <div class="px-3 py-2 flex items-center justify-between shrink-0">
          <span class="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
            Active Flags ({{ criteria.length }})
          </span>
          <button
            v-if="criteria.length"
            class="text-[11px] text-gray-500 hover:text-gray-800"
            @click="clearFlags"
          >Clear</button>
        </div>

        <div class="flex-1 overflow-y-auto min-h-0 px-3 pb-2">
          <p v-if="!criteria.length" class="text-[11px] text-gray-400 py-1">
            Click a flag above to add it.
          </p>
          <!-- One predicate row per active flag. The controls offered depend on
               the flag's kind: boolean = presence only, categorical = pick
               labelled codes, numeric = magnitude operators. -->
          <div
            v-for="c in criteria"
            :key="c.flag_id"
            class="py-1.5 border-b border-gray-200 last:border-0"
          >
            <div class="flex items-center gap-2">
              <span class="w-2 h-2 rounded-full shrink-0" :class="dotClass(flagOf(c).color)"></span>
              <span class="flex-1 min-w-0 text-xs text-gray-800 truncate" :title="flagOf(c).label">
                {{ flagOf(c).label }}
              </span>
              <span class="text-[11px] tabular-nums text-gray-500 shrink-0">
                {{ formatNumber(flagOf(c).on_count) }}
              </span>
              <button
                class="text-gray-400 hover:text-gray-700 text-sm leading-none px-1 shrink-0"
                title="Remove"
                @click="removeFlag(c.flag_id)"
              >&times;</button>
            </div>

            <div class="flex items-center gap-1 mt-1 pl-4">
              <select
                v-model="c.op"
                class="text-[11px] border border-gray-300 rounded px-1 py-0.5 bg-white"
                @change="onOpChange(c)"
              >
                <option v-for="o in opsFor(flagOf(c))" :key="o.value" :value="o.value">
                  {{ o.label }}
                </option>
              </select>

              <!-- numeric: one or two operands, in REAL units (scaled on send) -->
              <template v-if="needsOperands(c) === 1">
                <input
                  v-model="c.v1"
                  type="number"
                  step="any"
                  class="w-20 text-[11px] border border-gray-300 rounded px-1 py-0.5"
                  :placeholder="rangeHint(flagOf(c))"
                />
              </template>
              <template v-else-if="needsOperands(c) === 2">
                <input
                  v-model="c.v1"
                  type="number"
                  step="any"
                  class="w-16 text-[11px] border border-gray-300 rounded px-1 py-0.5"
                  placeholder="min"
                />
                <span class="text-[11px] text-gray-400">–</span>
                <input
                  v-model="c.v2"
                  type="number"
                  step="any"
                  class="w-16 text-[11px] border border-gray-300 rounded px-1 py-0.5"
                  placeholder="max"
                />
              </template>

              <!-- categorical: pick labelled codes, never a raw integer -->
              <div v-else-if="needsOperands(c) === 'codes'" class="flex flex-wrap gap-1">
                <button
                  v-for="v in flagOf(c).values"
                  :key="v.value"
                  type="button"
                  class="px-1.5 py-0.5 rounded border text-[10px]"
                  :class="c.codes.includes(v.value)
                    ? chipClass(v.color || flagOf(c).color, 'solid')
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-100'"
                  @click="toggleCode(c, v.value)"
                >{{ v.label }}</button>
              </div>
            </div>

            <p v-if="criterionError(c)" class="text-[10px] text-red-600 pl-4 mt-0.5">
              {{ criterionError(c) }}
            </p>
          </div>
        </div>

        <div class="px-3 pb-3 pt-1 shrink-0">
          <button
            type="button"
            class="w-full py-1.5 rounded text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            :disabled="!criteriaValid || loading"
            @click="runQuery"
          >{{ loading ? 'Querying...' : 'Query' }}</button>
          <p v-if="isStale" class="text-[10px] text-amber-700 mt-1 text-center">
            Criteria changed — click Query to refresh.
          </p>
        </div>
      </div>
    </div>

    <!-- RIGHT: patient list -->
    <div class="flex-1 min-w-0 flex flex-col min-h-0 bg-white">
      <div class="px-4 py-2 border-b border-gray-200 flex items-center justify-between shrink-0">
        <div class="text-xs text-gray-700">
          <template v-if="hasRun">
            <span class="font-semibold tabular-nums">{{ formatNumber(total) }}</span>
            <span class="text-gray-500">
              {{ total === 1 ? ' patient' : ' patients' }}
              <template v-if="queriedFilters.length > 1">
                matching all {{ queriedFilters.length }} conditions
              </template>
            </span>
            <span v-if="queriedFlagLabels" class="text-gray-400"> · {{ queriedFlagLabels }}</span>
          </template>
          <span v-else class="text-gray-400">No query run</span>
        </div>
        <button
          v-if="patients.length"
          class="text-[11px] text-gray-600 hover:text-gray-900 border border-gray-300 rounded px-2 py-1 disabled:opacity-40"
          :disabled="exporting"
          @click="exportCsv"
        >{{ exporting ? 'Exporting...' : 'Export CSV' }}</button>
      </div>

      <div class="flex-1 overflow-auto min-h-0">
        <div v-if="!hasRun && !loading" class="p-6 text-xs text-gray-500">
          Add one or more flags, then click Query.
        </div>
        <LoadingIndicator v-else-if="loading" message="Loading patients..." />
        <div v-else-if="error" class="m-4 bg-red-50 border border-red-200 rounded p-3">
          <p class="text-red-700 text-xs">{{ error }}</p>
          <button class="mt-2 text-xs text-red-600 underline" @click="runQuery">Retry</button>
        </div>
        <div v-else-if="!patients.length" class="p-6 text-xs text-gray-500">
          No patients carry all of the selected flags.
        </div>

        <table v-else class="w-full text-xs">
          <thead class="sticky top-0 bg-gray-50 z-10">
            <tr class="border-b border-gray-200 text-left text-gray-500">
              <th class="py-2 px-3 font-medium">Patient</th>
              <th class="py-2 px-3 font-medium">Patient ID</th>
              <th class="py-2 px-3 font-medium">DOB</th>
              <th class="py-2 px-3 font-medium">Age</th>
              <th class="py-2 px-3 font-medium">Flags</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="p in patients"
              :key="p.patient_identifier"
              class="border-b border-gray-100 hover:bg-gray-50 cursor-pointer align-top"
              @click="openPatient(p)"
            >
              <td class="py-1.5 px-3">
                <span class="font-medium text-gray-900">{{ p.name || '—' }}</span>
                <span
                  v-if="p.deceased"
                  class="ml-1 text-[10px] uppercase tracking-wide text-gray-400"
                >deceased</span>
              </td>
              <td class="py-1.5 px-3 tabular-nums text-gray-600">{{ p.patient_identifier }}</td>
              <td class="py-1.5 px-3 tabular-nums text-gray-700">{{ p.dob || '—' }}</td>
              <td class="py-1.5 px-3 tabular-nums text-gray-700">{{ p.age ?? '—' }}</td>
              <td class="py-1.5 px-3">
                <div class="flex flex-wrap gap-1">
                  <span
                    v-for="c in p.flags"
                    :key="c.flag_id"
                    class="inline-block px-1.5 py-0.5 rounded border text-[11px]"
                    :class="chipClass(c.color, c.style)"
                    :title="c.value_label || c.label"
                  >{{ c.label }}</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        v-if="hasRun && totalPages > 1"
        class="px-4 py-2 border-t border-gray-200 flex items-center justify-between shrink-0"
      >
        <button
          class="text-[11px] px-2 py-1 border border-gray-300 rounded disabled:opacity-40"
          :disabled="page <= 1"
          @click="goToPage(page - 1)"
        >Previous</button>
        <span class="text-[11px] text-gray-500">
          Page {{ page }} of {{ formatNumber(totalPages) }}
        </span>
        <button
          class="text-[11px] px-2 py-1 border border-gray-300 rounded disabled:opacity-40"
          :disabled="page >= totalPages"
          @click="goToPage(page + 1)"
        >Next</button>
      </div>
    </div>

    <!-- Patient drawer -->
    <div
      v-if="selectedPatient"
      class="fixed inset-0 z-40 bg-black/20"
      @click.self="selectedPatient = null"
    >
      <div class="absolute right-0 top-0 h-full w-[50%] min-w-[520px] bg-white shadow-xl overflow-auto">
        <Patient_Drawer
          :patient-identifier="selectedPatient.patient_identifier"
          :patient-data="selectedPatient"
          :patient-name="selectedPatient.name"
          :enabled-tabs="drawerEnabledTabs"
          :show-header="true"
          default-tab="details"
          metric-category="Care Management"
          @close="selectedPatient = null"
        />
      </div>
    </div>
  </div>
</template>

<script>
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import Patient_Drawer from '@/components/Patient_Drawer/Patient_Drawer.vue';

// Chip palettes, mirroring RiskAcuityBadge so a flag looks the same here as it
// does on the worklist. Full class strings (never interpolated) so Tailwind's
// JIT scanner can see them.
const LIGHT = {
  red: 'bg-red-100 text-red-700 border-red-300',
  orange: 'bg-orange-100 text-orange-700 border-orange-300',
  amber: 'bg-amber-100 text-amber-700 border-amber-300',
  yellow: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  blue: 'bg-blue-100 text-blue-700 border-blue-300',
  sky: 'bg-sky-100 text-sky-700 border-sky-300',
  teal: 'bg-teal-100 text-teal-700 border-teal-300',
  emerald: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  indigo: 'bg-indigo-100 text-indigo-700 border-indigo-300',
  violet: 'bg-violet-100 text-violet-700 border-violet-300',
  purple: 'bg-purple-100 text-purple-700 border-purple-300',
  pink: 'bg-pink-100 text-pink-700 border-pink-300',
  gray: 'bg-gray-100 text-gray-700 border-gray-300',
};
const SOLID = {
  red: 'bg-red-600 text-white border-red-600',
  orange: 'bg-orange-600 text-white border-orange-600',
  amber: 'bg-amber-600 text-white border-amber-600',
  yellow: 'bg-yellow-600 text-white border-yellow-600',
  blue: 'bg-blue-600 text-white border-blue-600',
  sky: 'bg-sky-600 text-white border-sky-600',
  teal: 'bg-teal-600 text-white border-teal-600',
  emerald: 'bg-emerald-600 text-white border-emerald-600',
  indigo: 'bg-indigo-600 text-white border-indigo-600',
  violet: 'bg-violet-600 text-white border-violet-600',
  purple: 'bg-purple-600 text-white border-purple-600',
  pink: 'bg-pink-600 text-white border-pink-600',
  gray: 'bg-gray-600 text-white border-gray-600',
};
const FAINT = {
  red: 'bg-red-50 text-red-500 border-red-200',
  orange: 'bg-orange-50 text-orange-500 border-orange-200',
  amber: 'bg-amber-50 text-amber-500 border-amber-200',
  yellow: 'bg-yellow-50 text-yellow-500 border-yellow-200',
  blue: 'bg-blue-50 text-blue-500 border-blue-200',
  sky: 'bg-sky-50 text-sky-500 border-sky-200',
  teal: 'bg-teal-50 text-teal-500 border-teal-200',
  emerald: 'bg-emerald-50 text-emerald-500 border-emerald-200',
  indigo: 'bg-indigo-50 text-indigo-500 border-indigo-200',
  violet: 'bg-violet-50 text-violet-500 border-violet-200',
  purple: 'bg-purple-50 text-purple-500 border-purple-200',
  pink: 'bg-pink-50 text-pink-500 border-pink-200',
  gray: 'bg-gray-50 text-gray-500 border-gray-200',
};
const STYLE_MAPS = { solid: SOLID, light: LIGHT, faint: FAINT };

// Solid dot beside each flag in the left list — the flag's own colour, so the
// list reads the same as the chips on the right.
const DOT = {
  red: 'bg-red-500', orange: 'bg-orange-500', amber: 'bg-amber-500',
  yellow: 'bg-yellow-500', blue: 'bg-blue-500', sky: 'bg-sky-500',
  teal: 'bg-teal-500', emerald: 'bg-emerald-500', indigo: 'bg-indigo-500',
  violet: 'bg-violet-500', purple: 'bg-purple-500', pink: 'bg-pink-500',
  gray: 'bg-gray-400',
};

// Operator menus per flag kind. Magnitude operators are deliberately absent
// from the categorical set: a categorical VALUE is a CODE, and codes carry
// sentinels (lace_score 999 = "No Admit Date"), so `>= 8` would silently
// include no-admit patients. The backend rejects it too; this stops the user
// ever reaching for it.
const OPS_BOOLEAN = [
  { value: 'present', label: 'has' },
  { value: 'not_present', label: 'does not have' },
];
const OPS_CATEGORICAL = [
  { value: 'present', label: 'has (any)' },
  { value: 'in', label: 'is' },
  { value: 'not_in', label: 'is not' },
  { value: 'not_present', label: 'does not have' },
];
const OPS_NUMERIC = [
  { value: 'present', label: 'has (any)' },
  { value: 'gte', label: '\u2265' },
  { value: 'lte', label: '\u2264' },
  { value: 'gt', label: '>' },
  { value: 'lt', label: '<' },
  { value: 'eq', label: '=' },
  { value: 'between', label: 'between' },
  { value: 'not_present', label: 'does not have' },
];
const TWO_OPERAND_OPS = ['between'];
const ONE_OPERAND_OPS = ['gte', 'lte', 'gt', 'lt', 'eq', 'ne'];
const CODE_OPS = ['in', 'not_in'];

const PAGE_SIZE = 100;
const EXPORT_PAGE_SIZE = 1000;

export default {
  name: 'CareManagementRegistry',
  components: { LoadingIndicator, Patient_Drawer },
  data() {
    return {
      flags: [],
      flagsLoading: false,
      flagsError: null,
      // Staged criteria: one entry per active flag.
      //   {flag_id, op, v1, v2, codes}
      // v1/v2 are held in REAL units (A1C 9.0, not the stored 90); scaling is
      // applied only when building the request.
      criteria: [],
      queriedFilters: [],    // the payload the CURRENT results were built from
      hasRun: false,
      flagSearch: '',

      patients: [],
      total: 0,
      page: 1,
      pageSize: PAGE_SIZE,
      loading: false,
      error: null,

      exporting: false,
      selectedPatient: null,
      drawerEnabledTabs: [
        'details', 'utilization', 'programs',
        'gaps', 'engagement', 'care_plan', 'care_team',
      ],
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.pageSize));
    },
    selectedFlagIds() {
      return this.criteria.map((c) => c.flag_id);
    },
    // Available = not yet staged, honouring the filter box.
    availableFlags() {
      const q = this.flagSearch.trim().toLowerCase();
      const staged = this.selectedFlagIds;
      return this.flags.filter((f) => {
        if (staged.includes(f.flag_id)) return false;
        if (!q) return true;
        return f.label.toLowerCase().includes(q) || f.key.toLowerCase().includes(q);
      });
    },
    queriedFlags() {
      return this.queriedFilters
        .map((f) => this.flags.find((x) => x.flag_id === f.flag_id))
        .filter(Boolean);
    },
    queriedFlagLabels() {
      return this.queriedFilters
        .map((f) => this.describeFilter(f))
        .filter(Boolean)
        .join(', ');
    },
    // Every staged criterion is complete enough to send.
    criteriaValid() {
      return this.criteria.length > 0
        && this.criteria.every((c) => !this.criterionError(c));
    },
    // Staged criteria no longer match what produced the visible results.
    isStale() {
      if (!this.hasRun) return false;
      return JSON.stringify(this.buildFilters()) !== JSON.stringify(this.queriedFilters);
    },
  },
  mounted() {
    this.fetchFlags();
  },
  methods: {
    async fetchFlags() {
      this.flagsLoading = true;
      this.flagsError = null;
      try {
        const { data } = await this.$axios.get('/api/patient-registry/flags/');
        this.flags = data.flags || [];
      } catch (err) {
        this.flagsError = err.response?.data?.error || 'Failed to load flags';
      } finally {
        this.flagsLoading = false;
      }
    },
    // Paging reuses whatever criteria the visible results were built from
    // (queriedFlagIds), never the staged tray — so paging can't silently
    // switch cohorts under a stale set of criteria.
    async fetchRegistry() {
      if (!this.queriedFilters.length) {
        this.patients = [];
        this.total = 0;
        return;
      }
      this.loading = true;
      this.error = null;
      try {
        const { data } = await this.$axios.post('/api/patient-registry/', {
          filters: this.queriedFilters,
          page: this.page,
          page_size: this.pageSize,
        });
        this.patients = data.patients || [];
        this.total = data.total || 0;
      } catch (err) {
        this.error = err.response?.data?.error || 'Failed to load registry';
        this.patients = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },
    runQuery() {
      if (!this.criteriaValid) return;
      this.queriedFilters = this.buildFilters();
      this.hasRun = true;
      this.page = 1;
      this.fetchRegistry();
    },
    addFlag(flagId) {
      if (this.selectedFlagIds.includes(flagId)) return;
      this.criteria.push({ flag_id: flagId, op: 'present', v1: '', v2: '', codes: [] });
    },
    removeFlag(flagId) {
      const i = this.criteria.findIndex((c) => c.flag_id === flagId);
      if (i !== -1) this.criteria.splice(i, 1);
    },
    clearFlags() {
      this.criteria = [];
    },

    // ---- predicate helpers ------------------------------------------------
    flagOf(c) {
      return this.flags.find((f) => f.flag_id === c.flag_id) || { label: '?', values: [] };
    },
    opsFor(flag) {
      if (flag.kind === 'categorical') return OPS_CATEGORICAL;
      if (flag.kind === 'numeric') return OPS_NUMERIC;
      return OPS_BOOLEAN;
    },
    // What operand control this criterion needs: 0 | 1 | 2 | 'codes'
    needsOperands(c) {
      if (CODE_OPS.includes(c.op)) return 'codes';
      if (TWO_OPERAND_OPS.includes(c.op)) return 2;
      if (ONE_OPERAND_OPS.includes(c.op)) return 1;
      return 0;
    },
    onOpChange(c) {
      // Operands from the previous operator are meaningless under the new one.
      c.v1 = '';
      c.v2 = '';
      c.codes = [];
    },
    toggleCode(c, value) {
      const i = c.codes.indexOf(value);
      if (i === -1) c.codes.push(value);
      else c.codes.splice(i, 1);
    },
    rangeHint(flag) {
      if (flag.min_value == null || flag.max_value == null) return '';
      const sc = flag.scale || 1;
      return `${flag.min_value / sc}–${flag.max_value / sc}`;
    },
    // Per-row validation. Returns a message, or '' when the row is sendable.
    criterionError(c) {
      const need = this.needsOperands(c);
      if (need === 'codes') {
        return c.codes.length ? '' : 'Select at least one value.';
      }
      if (need >= 1 && (c.v1 === '' || c.v1 === null || Number.isNaN(Number(c.v1)))) {
        return 'Enter a number.';
      }
      if (need === 2) {
        if (c.v2 === '' || c.v2 === null || Number.isNaN(Number(c.v2))) return 'Enter both bounds.';
      }
      return '';
    },
    // Build the API payload. Real units are multiplied by the flag's scale here
    // and ONLY here — the user always types 9.0, the wire always carries 90.
    buildFilters() {
      return this.criteria.map((c) => {
        const flag = this.flagOf(c);
        const sc = flag.scale || 1;
        const need = this.needsOperands(c);
        if (need === 'codes') {
          return { flag_id: c.flag_id, op: c.op, values: [...c.codes] };
        }
        if (need === 2) {
          return {
            flag_id: c.flag_id,
            op: c.op,
            values: [Math.round(Number(c.v1) * sc), Math.round(Number(c.v2) * sc)],
          };
        }
        if (need === 1) {
          return { flag_id: c.flag_id, op: c.op, value: Math.round(Number(c.v1) * sc) };
        }
        return { flag_id: c.flag_id, op: c.op };
      });
    },
    // Human-readable summary of an executed filter, for the results header.
    describeFilter(f) {
      const flag = this.flags.find((x) => x.flag_id === f.flag_id);
      if (!flag) return '';
      const sc = flag.scale || 1;
      const opLabel = (this.opsFor(flag).find((o) => o.value === f.op) || {}).label || f.op;
      if (f.op === 'present') return flag.label;
      if (f.op === 'not_present') return `no ${flag.label}`;
      if (CODE_OPS.includes(f.op)) {
        const names = (f.values || [])
          .map((v) => (flag.values.find((x) => x.value === v) || {}).label || v)
          .join('/');
        return `${flag.label} ${opLabel} ${names}`;
      }
      if (f.op === 'between') {
        return `${flag.label} ${f.values[0] / sc}–${f.values[1] / sc}`;
      }
      return `${flag.label} ${opLabel} ${f.value / sc}`;
    },
    goToPage(p) {
      if (p < 1 || p > this.totalPages) return;
      this.page = p;
      this.fetchRegistry();
    },
    openPatient(p) {
      this.selectedPatient = p;
    },
    // Export the WHOLE cohort, not just the page on screen — a registry you
    // can only export 100 rows of isn't a registry.
    async exportCsv() {
      this.exporting = true;
      try {
        const rows = [];
        const pages = Math.ceil(this.total / EXPORT_PAGE_SIZE);
        for (let p = 1; p <= pages; p += 1) {
          const { data } = await this.$axios.post('/api/patient-registry/', {
            filters: this.queriedFilters,
            page: p,
            page_size: EXPORT_PAGE_SIZE,
          });
          rows.push(...(data.patients || []));
        }
        this.downloadCsv(rows);
      } catch (err) {
        this.error = err.response?.data?.error || 'Export failed';
      } finally {
        this.exporting = false;
      }
    },
    downloadCsv(rows) {
      const header = ['Patient ID', 'Last Name', 'First Name', 'DOB', 'Age', 'State', 'Deceased', 'Dual', 'Flags'];
      const esc = (v) => {
        const s = v === null || v === undefined ? '' : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const lines = [header.join(',')];
      rows.forEach((p) => {
        lines.push([
          p.patient_identifier, p.last_name, p.first_name, p.dob, p.age,
          p.state, p.deceased ? 'Yes' : 'No', p.dual ? 'Yes' : 'No',
          (p.flags || []).map((c) => c.label).join('; '),
        ].map(esc).join(','));
      });
      const names = this.queriedFlags.map((f) => f.key).join('_');
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `registry_${names || 'patients'}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
    dotClass(color) {
      return DOT[color] || DOT.gray;
    },
    chipClass(color, style) {
      const map = STYLE_MAPS[style] || LIGHT;
      return map[color] || map.gray || LIGHT.gray;
    },
    formatNumber(n) {
      return n == null ? '0' : Number(n).toLocaleString();
    },
  },
};
</script>
